import './style.scss'
import React, { useContext, useEffect, useState } from 'react'
import {
    FiMail, FiPhone, FiCalendar, FiCreditCard, FiUser, FiFileText,
} from 'react-icons/fi'
import { BackLink, Box, Flex, Text, Card, tokens } from 'src/componentv2'
import { fontBold, fontMedium } from 'src/model/v2/interface/constants'
import { StatusTag } from '../components/StatusTag'
import CustomerApiContext, { CustomerProfileResponse } from 'src/provider/API/v2/call-service/Customer/index'
import dateTime from 'src/utils/dateTime'

function getInitials(name: string) {
    const parts = name.trim().split(/\s+/).filter(Boolean)
    if (parts.length === 0) return '—'
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
    return (parts[0][0] + parts[1][0]).toUpperCase()
}

function fmtDate(iso: string | null) {
    const d = dateTime.parseUtc(iso)
    if (!d) return '—'
    return d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
}

function fmtMoney(n: number | null | undefined) {
    if (n === null || n === undefined) return '—'
    return `₦${n.toLocaleString()}`
}

function DetailTile({
                        icon: Icon, label, value,
                    }: { icon: React.ElementType; label: string; value: string }) {
    return (
        <Flex items="center" gap="1.2" style={{ padding: '1.6rem' }}>
            <Box
                width="3.6" height="3.6" radius="0.8"
                bgColor={tokens.pageBg}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
            >
                <Icon size={16} color={tokens.textMuted} />
            </Box>
            <Flex direction="column" gap="0.2" items="flex-start" style={{ minWidth: 0 }}>
                <Text fontSize="1.2" color={tokens.textMuted} style={fontMedium}>{label}</Text>
                <Text fontSize="1.4" style={fontMedium} color={tokens.textDark} className="customer-detail__stat-value">{value}</Text>
            </Flex>
        </Flex>
    )
}

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <Card padding="0" radius="1.2" borderColor={tokens.border} shadow={tokens.shadowCard} topMargin="1.6" className="customer-detail__summary-card">
            <Box style={{ padding: '1.6rem 2rem', borderBottom: `1px solid ${tokens.border}` }}>
                <Text fontSize="1.6" style={fontMedium} color={tokens.textDark}>{title}</Text>
            </Box>
            <div className="customer-detail__stats-grid">
                {children}
            </div>
        </Card>
    )
}

function DocRow({ label, url }: { label: string; url: string | null }) {
    return (
        <Flex justify="space-between" items="center" style={{ padding: '1.2rem 2rem', borderBottom: `1px solid ${tokens.border}` }}>
            <Flex items="center" gap="0.8">
                <FiFileText size={16} color={tokens.textMuted} />
                <Text fontSize="1.4" color={tokens.textDark}>{label}</Text>
            </Flex>
            {url ? (
                <a href={url} target="_blank" rel="noreferrer">
                    <Text fontSize="1.4" style={fontMedium} color={tokens.primary}>View</Text>
                </a>
            ) : (
                <Text fontSize="1.4" color={tokens.textMuted}>Not uploaded</Text>
            )}
        </Flex>
    )
}

export function CustomerDetail({
                                   profileId, onBack,
                               }: { profileId: number; onBack: () => void }) {
    const { getCustomerProfile } = useContext(CustomerApiContext)
    const [data, setData] = useState<CustomerProfileResponse | null>(null)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        let cancelled = false
        setLoading(true)
        ;(async () => {
            const result = await getCustomerProfile(profileId)
            if (!cancelled) {
                setData(result || null)
                setLoading(false)
            }
        })()
        return () => { cancelled = true }
    }, [profileId])

    const header = (
        <Flex items="center" gap="1.2">
            <BackLink onClick={onBack} color="#000000">{''}</BackLink>
            <Text fontSize="2.4" style={fontBold} color={tokens.textDark}>Customer Details</Text>
        </Flex>
    )

    if (loading) {
        return (
            <div className="customer-detail" style={{ padding: '2.4rem 3.2rem' }}>
                {header}
                <Box topMargin="2.4">
                    <Text fontSize="1.4" color={tokens.textMuted}>Loading customer profile…</Text>
                </Box>
            </div>
        )
    }

    if (!data) {
        return (
            <div className="customer-detail" style={{ padding: '2.4rem 3.2rem' }}>
                {header}
                <Box topMargin="2.4">
                    <Text fontSize="1.4" color={tokens.textMuted}>Couldn't load this customer's profile.</Text>
                </Box>
            </div>
        )
    }

    const { profile, transactionLimit, documents } = data
    const fullName = profile.fullName
        || [profile.salutation, profile.firstName, profile.middleName, profile.lastName].filter(Boolean).join(' ')
        || profile.username
        || `Profile #${profile.profileId}`

    const accountStatus = profile.onboardingCompleted === false ? 'Pending' : 'Active'

    return (
        <div className="customer-detail" style={{ padding: '2.4rem 3.2rem' }}>
            {header}

            {/* Summary strip */}
            <Card
                padding="0" radius="1.2" borderColor={tokens.border} shadow={tokens.shadowCard} topMargin="2.4"
                className="customer-detail__summary-card"
            >
                <Flex
                    justify="space-between" items="flex-start"
                    style={{ padding: '2rem 2.4rem', borderBottom: `1px solid ${tokens.border}` }}
                >
                    <Flex items="center" gap="1.2">
                        <Box
                            width="4.8" height="4.8" radius="999"
                            bgColor={tokens.primarySoft}
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden' }}
                        >
                            {profile.profilePictureUrl ? (
                                <img src={profile.profilePictureUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (
                                <Text fontSize="1.6" style={fontBold} color={tokens.primaryDark}>{getInitials(fullName)}</Text>
                            )}
                        </Box>
                        <Flex direction="column" gap="0.4" items="flex-start">
                            <Text fontSize="2" style={fontBold} color={tokens.textDark}>{fullName}</Text>
                            <Text fontSize="1.4" color={tokens.textMuted}>{profile.customerId || `Profile #${profile.profileId}`}</Text>
                        </Flex>
                    </Flex>
                    <StatusTag status={accountStatus} />
                </Flex>
            </Card>

            {/* 1. Personal Information */}
            <SectionCard title="Personal Information">
                <DetailTile icon={FiUser} label="Full Name" value={fullName} />
                <DetailTile icon={FiMail} label="Email" value={profile.emailAddress || '—'} />
                <DetailTile icon={FiPhone} label="Phone" value={profile.phoneNumber || '—'} />
                <DetailTile icon={FiUser} label="Gender" value={profile.gender || '—'} />
                <DetailTile icon={FiCalendar} label="Date of Birth" value={profile.dateOfBirth || '—'} />
                <DetailTile icon={FiUser} label="Address" value={profile.fullAddress || '—'} />
                <DetailTile icon={FiCreditCard} label="BVN" value={profile.bvn || '—'} />
                <DetailTile icon={FiCreditCard} label={profile.idType || 'ID'} value={profile.nin || profile.bvn || '—'} />
            </SectionCard>

            {/* 2. Account Information */}
            <SectionCard title="Account Information">
                <DetailTile icon={FiUser} label="Username" value={profile.username || '—'} />
                <DetailTile icon={FiCreditCard} label="Primary Account No." value={profile.primaryAccountNumber || '—'} />
                <DetailTile icon={FiUser} label="Onboarding Method" value={profile.onboardingMethod || '—'} />
                <DetailTile icon={FiCalendar} label="Onboarding Date" value={fmtDate(profile.onboardingDate)} />
                <DetailTile icon={FiCalendar} label="Last Login" value={fmtDate(profile.lastLoginDate)} />
                <DetailTile
                    icon={FiUser}
                    label="Onboarding Status"
                    value={profile.onboardingCompleted === true ? 'Completed' : profile.onboardingCompleted === false ? 'Pending' : '—'}
                />
                <DetailTile icon={FiUser} label="Instant Payment" value={profile.isInstantPaymentEnabled ? 'Enabled' : 'Disabled'} />
                <DetailTile icon={FiUser} label="Account Upgrade Status" value={profile.accountUpgradeStatus || '—'} />
            </SectionCard>

            {/* 3. Uploaded Documents */}
            <Card padding="0" radius="1.2" borderColor={tokens.border} shadow={tokens.shadowCard} topMargin="1.6" className="customer-detail__summary-card">
                <Box style={{ padding: '1.6rem 2rem', borderBottom: `1px solid ${tokens.border}` }}>
                    <Text fontSize="1.6" style={fontMedium} color={tokens.textDark}>Uploaded Documents</Text>
                </Box>
                {documents ? (
                    <>
                        <DocRow label="Signature" url={documents.signature} />
                        <DocRow label="National ID Card" url={documents.nationalIdCard} />
                        <DocRow label="Utility Document" url={documents.utilityDocument} />
                        <DocRow label="Passport" url={documents.passPort} />
                    </>
                ) : (
                    <Box style={{ padding: '1.6rem 2rem' }}>
                        <Text fontSize="1.4" color={tokens.textMuted}>No documents on file</Text>
                    </Box>
                )}
            </Card>

            {/* 4. Transaction Limit */}
            <Card padding="2rem" radius="1.2" borderColor={tokens.border} shadow={tokens.shadowCard} topMargin="1.6">
                <Text fontSize="1.6" style={fontMedium} color={tokens.textDark}>Transaction Limit</Text>

                <Box topMargin="1.6">
                    <Text fontSize="1.3" style={fontMedium} color={tokens.textMuted}>App Limit</Text>
                    {transactionLimit.appLimitIsUnavailable || !transactionLimit.appLimit ? (
                        <Text topMargin="0.4" fontSize="1.4" color={tokens.textMuted}>Not available</Text>
                    ) : (
                        <Flex topMargin="0.8" gap="2.4" wrap="wrap">
                            <Flex direction="column" gap="0.2" items="flex-start">
                                <Text fontSize="1.2" color={tokens.textMuted}>One-time Limit</Text>
                                <Text fontSize="1.4" style={fontMedium} color={tokens.textDark}>{fmtMoney(transactionLimit.appLimit.oneTimeLimit)}</Text>
                            </Flex>
                            <Flex direction="column" gap="0.2" items="flex-start">
                                <Text fontSize="1.2" color={tokens.textMuted}>Daily Limit</Text>
                                <Text fontSize="1.4" style={fontMedium} color={tokens.textDark}>{fmtMoney(transactionLimit.appLimit.dailyLimit)}</Text>
                            </Flex>
                        </Flex>
                    )}
                </Box>

                <Box topMargin="2">
                    <Text fontSize="1.3" style={fontMedium} color={tokens.textMuted}>BankOne Limit</Text>
                    {transactionLimit.bankOneLimit.isUnavailable ? (
                        <Text topMargin="0.4" fontSize="1.4" color={tokens.textMuted}>Not available</Text>
                    ) : (
                        <Flex topMargin="0.8" gap="2.4" wrap="wrap">
                            <Flex direction="column" gap="0.2" items="flex-start">
                                <Text fontSize="1.2" color={tokens.textMuted}>Tier</Text>
                                <Text fontSize="1.4" style={fontMedium} color={tokens.textDark}>{transactionLimit.bankOneLimit.tier || '—'}</Text>
                            </Flex>
                            <Flex direction="column" gap="0.2" items="flex-start">
                                <Text fontSize="1.2" color={tokens.textMuted}>Max Balance</Text>
                                <Text fontSize="1.4" style={fontMedium} color={tokens.textDark}>{fmtMoney(transactionLimit.bankOneLimit.maximumBalance)}</Text>
                            </Flex>
                            <Flex direction="column" gap="0.2" items="flex-start">
                                <Text fontSize="1.2" color={tokens.textMuted}>Max Deposit</Text>
                                <Text fontSize="1.4" style={fontMedium} color={tokens.textDark}>{fmtMoney(transactionLimit.bankOneLimit.maximumDeposit)}</Text>
                            </Flex>
                        </Flex>
                    )}
                </Box>
            </Card>
        </div>
    )
}

export default CustomerDetail